import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Icon, StatTile, SectionHead, Panel, MiniKpi, Bars, Empty, Pager, T, chartBase, gridBase, post } from "./SwachhataDashboard";
import { EWASTE_CATEGORIES, EWASTE_ITEM_LABEL, CO_CURRICULAR, AGE_GROUPS } from "../ewasteCatalog";
import "./SwachhataDashboard.css";
import "./EWasteDashboard.css";

/*
 * /admin/e-waste-drive — E-Waste Collection Drive command centre (same design
 * system as the Swachhata Hi Seva dashboard, prefix swx- + ewd-).
 *
 *   1. Overview — States / UTs (districts, for a state) with schools reported,
 *                 items collected per category, corners, participants; drills.
 *   2. Items    — category share, daily trend, item-wise quantities per
 *                 category, names schools gave their "other" items.
 *   3. Schools  — searchable register of every report, full Excel export.
 *
 * Central admin (role 1): national → state → district. State admin (role 4):
 * locked to its own state server-side. Aggregates are cached on the server
 * (overview 2 min, items 10 min); the register is paginated.
 */

const CAT = Object.fromEntries(EWASTE_CATEGORIES.map((c) => [c.code, c]));
const CODES = EWASTE_CATEGORIES.map((c) => c.code);
const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-IN"));
const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `${(v / 1e7).toFixed(2).replace(/\.?0+$/, "")} Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(2).replace(/\.?0+$/, "")} L`;
  return v.toLocaleString("en-IN");
};
const pct = (n, d) => (d ? Math.round(((n || 0) / d) * 1000) / 10 : 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");
const titleCase = (s) => String(s || "").toLowerCase().replace(/(^|[\s(&/-])([a-z])/g, (m, p, c) => p + c.toUpperCase());
const cornerLabel = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : v === "na" ? "Not applicable" : "—");
const ccLabel = (k) => CO_CURRICULAR.find((c) => c.key === k)?.label || "";

export default function EWasteDashboard() {
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const isStateAdmin = user?.role === 4;

  const empty = { state: "", district: "", from: "", to: "" };
  const [draft, setDraft] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const scope = useCallback((f) => {
    const p = {};
    ["state", "district", "from", "to"].forEach((k) => { if (f[k]) p[k] = f[k]; });
    return p;
  }, []);

  const [overview, setOverview] = useState(null);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [areaSort, setAreaSort] = useState("items");
  const [areaQuery, setAreaQuery] = useState("");

  const fetchAll = useCallback(async (f) => {
    setLoading(true); setError("");
    const [o, i] = await Promise.allSettled([post("swachhata/ewaste/admin/overview", scope(f)), post("swachhata/ewaste/admin/items", scope(f))]);
    if (o.status === "fulfilled") {
      setOverview(o.value);
      if (!f.state && !o.value.scope?.locked) setStateOptions(o.value.rows.filter((x) => x.key));
    }
    if (i.status === "fulfilled") setItems(i.value);
    const failed = [o, i].find((x) => x.status === "rejected");
    if (failed) setError(failed.reason?.response?.data?.message || failed.reason?.message || "Some statistics could not be loaded — please refresh.");
    setLoading(false);
  }, [scope]);
  useEffect(() => { fetchAll(applied); }, [applied, fetchAll]);

  // ── school register ──
  const [rq, setRq] = useState({ search: "", collected: "", sortBy: "submittedAt", page: 1 });
  const [list, setList] = useState({ rows: [], total: 0, totalPages: 1, page: 1 });
  const [listLoading, setListLoading] = useState(false);
  const [exporting, setExporting] = useState(""); // "" | "xlsx" | "pdf"
  const listQuery = useCallback((f, q, extra = {}) => ({
    ...scope(f), page: q.page, limit: 25, sortBy: q.sortBy, sortDir: "desc",
    ...(q.search ? { search: q.search } : {}), ...(q.collected ? { collected: q.collected } : {}), ...extra,
  }), [scope]);
  useEffect(() => {
    const t = setTimeout(async () => {
      setListLoading(true);
      try {
        const d = await post("swachhata/ewaste/admin/schools", listQuery(applied, rq));
        setList({ rows: d.rows, total: d.total, totalPages: d.totalPages, page: d.page });
      } catch (e) { /* the stats banner reports failures */ }
      finally { setListLoading(false); }
    }, rq.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [applied, rq, listQuery]);

  const level = overview?.level || (applied.state ? "district" : "state");
  const lockedState = overview?.scope?.locked ? overview.scope.state : null;
  const apply = (next) => { setApplied(next); setDraft(next); setRq((q) => ({ ...q, page: 1 })); };
  const drill = (row) => {
    if (!row.key) return;
    if (level === "state" && !isStateAdmin) apply({ ...applied, state: row.key, district: "" });
    else if (level === "district" && !applied.district) apply({ ...applied, district: row.key });
  };
  const scopeName = lockedState || (applied.state ? titleCase(applied.state) : "All States/UTs/ABs");
  const crumbs = [
    !isStateAdmin && { label: "India", onClick: applied.state ? () => apply({ ...applied, state: "", district: "" }) : null },
    (applied.state || lockedState) && { label: lockedState || titleCase(applied.state), onClick: applied.district ? () => apply({ ...applied, district: "" }) : null },
    applied.district && { label: titleCase(applied.district), onClick: null },
  ].filter(Boolean);

  const ot = overview?.totals || {};
  const areaWord = level === "district" ? "District" : "States/UTs/ABs";
  const areaWordPlural = level === "district" ? "Districts" : "States/UTs/ABs";
  const areaRows = useMemo(() => {
    const q = areaQuery.trim().toLowerCase();
    const rows = (overview?.rows || []).filter((r) => !q || r.name.toLowerCase().includes(q));
    const by = {
      items: (a, b) => b.items - a.items || b.schools - a.schools,
      schools: (a, b) => b.schools - a.schools || b.items - a.items,
      participants: (a, b) => b.participants - a.participants,
      name: (a, b) => a.name.localeCompare(b.name),
    }[areaSort];
    return [...rows].sort(by);
  }, [overview, areaQuery, areaSort]);
  const maxItems = Math.max(1, ...areaRows.map((r) => r.items));
  const maxSchools = Math.max(1, ...areaRows.map((r) => r.schools));

  // ── charts ──
  const catTotals = CODES.map((c) => ot[c] || 0);
  const donutOptions = useMemo(() => ({
    chart: { ...chartBase, type: "donut" },
    labels: EWASTE_CATEGORIES.map((c) => `${c.code} · ${c.short}`),
    colors: EWASTE_CATEGORIES.map((c) => c.tone),
    legend: { position: "bottom", fontWeight: 600 },
    dataLabels: { enabled: false },
    stroke: { width: 3, colors: ["#fff"] },
    tooltip: { y: { formatter: (v) => `${fmt(v)} items` } },
    plotOptions: { pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Items", formatter: () => compact(ot.items || 0) }, value: { fontWeight: 800 } } } } },
  }), [ot.items]);
  const trend = overview?.trend || [];
  const trendOptions = useMemo(() => ({
    chart: { ...chartBase, type: "area" },
    colors: [T.blue, T.orange],
    stroke: { curve: "smooth", width: [2.6, 2.2] },
    fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95] } },
    dataLabels: { enabled: false },
    grid: gridBase,
    legend: { position: "top", horizontalAlign: "right", fontWeight: 600 },
    xaxis: { categories: trend.map((t) => t.date), labels: { formatter: fmtDay, style: { fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
    yaxis: [
      { seriesName: "Schools reporting", min: 0, forceNiceScale: true, labels: { formatter: (v) => compact(Math.round(v)) }, title: { text: "Schools" } },
      { seriesName: "Items collected", opposite: true, min: 0, forceNiceScale: true, labels: { formatter: (v) => compact(Math.round(v)) }, title: { text: "Items" } },
    ],
    tooltip: { shared: true, intersect: false, x: { formatter: (v, o) => new Date(trend[o?.dataPointIndex]?.date || v).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) } },
  }), [trend]);
  const trendSeries = useMemo(() => [{ name: "Schools reporting", data: trend.map((t) => t.reports) }, { name: "Items collected", data: trend.map((t) => t.items) }], [trend]);

  const itemCats = items?.categories || [];
  const cornerRows = [["yes", "Yes", T.green], ["no", "No", T.red], ["na", "Not applicable", T.slate]];

  // ── exports ──
  const stamp = () => new Date().toISOString().slice(0, 10);
  const scopeFile = () => [applied.state, applied.district].filter(Boolean).map(titleCase).join("_").replace(/\s+/g, "_") || "National";
  const exportAreas = () => {
    const ws = XLSX.utils.json_to_sheet(areaRows.map((r, i) => ({
      "S.No": i + 1, [areaWord]: r.name, "Schools reported": r.schools, "Schools that collected E-Waste": r.collectedSchools, "Total items collected": r.items,
      ...Object.fromEntries(EWASTE_CATEGORIES.map((c) => [`${c.code} (${c.short})`, r[c.code]])),
      "Collection corners set up": r.corners, "Workshops": r.workshops, "Students (3–18)": r.students, "Teachers (18+)": r.teachers, "Total participants": r.participants, "Photos": r.photos,
    })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, level === "district" ? "Districts" : "States-UTs-ABs");
    XLSX.writeFile(wb, `Special_Campaign_6_EWaste_${level === "district" ? "Districts" : "States_UTs_ABs"}_${scopeFile()}_${stamp()}.xlsx`);
  };

  // The category-wise report: the quantities of every category of the E-Waste
  // (Management) Rules, 2022, each with the share it carries, and the names
  // schools gave the items they entered themselves. Laid out as numbered
  // tables so it reads as a document rather than a dashboard dump.
  const exportAreasPdf = () => {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const M = 42;
    const INK = [15, 36, 24];
    const HEAD = [223, 235, 226];
    const RULE = [168, 186, 174];
    const share = (part, whole) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "0.0%");
    const asOn = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");

    // every category's rows, so the summary and the detail tables agree
    const detail = EWASTE_CATEGORIES.filter((c) => !c.freeform).map((c) => {
      const data = itemCats.find((x) => x.code === c.code);
      const rows = (data?.items || [])
        .map((it) => ({ label: EWASTE_ITEM_LABEL[`${c.code}:${it.key}`] || it.key, qty: it.qty, entries: it.entries }))
        .sort((a, b) => b.qty - a.qty);
      return { c, rows, total: rows.reduce((t, r) => t + r.qty, 0) };
    });
    // OTH has no sub-categories: the school types each name, so the table is
    // the names themselves, with everything past the listed ones as one row.
    const othTotal = itemCats.find((x) => x.code === "OTH")?.total || 0;
    const othNamed = (items?.otherNames || []).filter((n) => n.category === "OTH");
    const othListed = othNamed.reduce((t, n) => t + n.qty, 0);
    const othRows = [
      ...othNamed.map((n) => ({ label: n.name, qty: n.qty, entries: n.entries })),
      ...(othTotal > othListed ? [{ label: "Other names reported by schools (not individually listed)", qty: othTotal - othListed, entries: null }] : []),
    ];
    const grand = detail.reduce((t, d) => t + d.total, 0) + othTotal;

    // title block
    doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(13);
    doc.text("ECO CLUBS FOR MISSION LiFE", W / 2, 54, { align: "center" });
    doc.setFontSize(11.5);
    doc.text("SPECIAL CAMPAIGN 6 — E-WASTE COLLECTION DRIVE", W / 2, 72, { align: "center" });
    doc.setFontSize(10.5);
    doc.text("CATEGORY-WISE REPORT", W / 2, 88, { align: "center" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(95, 115, 104);
    doc.text(`(Report as on ${asOn})`, W / 2, 104, { align: "center" });
    doc.text(scopeName, W / 2, 118, { align: "center" });

    const base = {
      styles: { fontSize: 8.5, cellPadding: 4, lineColor: RULE, lineWidth: 0.5, textColor: INK, valign: "middle" },
      headStyles: { fillColor: HEAD, textColor: INK, fontStyle: "bold", fontSize: 8.5, halign: "center" },
      footStyles: { fillColor: [241, 247, 242], textColor: INK, fontStyle: "bold" },
      margin: { left: M, right: M, top: 52 },
      theme: "grid",
      showFoot: "lastPage",
    };
    // autoTable does not carry columnStyles into the foot, so the total row
    // has to say for itself that the figures are right-aligned
    const R = (v) => ({ content: v, styles: { halign: "right" } });
    // a caption that never gets stranded at the foot of a page
    let y = 136;
    const caption = (text) => {
      if (y > H - 120) { doc.addPage(); y = 52; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor(...INK);
      doc.text(text, M, y);
      y += 10;
    };
    const after = () => { y = doc.lastAutoTable.finalY + 26; };

    // Table 1 — the five categories, what each carries and how many rows it has
    caption("Table 1 – Category-wise summary");
    doc.autoTable({
      ...base,
      startY: y,
      head: [["S. No.", "Category", "Items collected", "Share", "Sub-categories / items"]],
      body: [
        ...detail.map((d, i) => [i + 1, `${d.c.title} (${d.c.code})`, fmt(d.total), share(d.total, grand), fmt(d.rows.length)]),
        [detail.length + 1, "Others – items named by schools (OTH)", fmt(othTotal), share(othTotal, grand), fmt(othRows.length)],
      ],
      foot: [["", "Total", R(fmt(grand)), R("100.0%"), R(fmt(detail.reduce((t, d) => t + d.rows.length, 0) + othRows.length))]],
      columnStyles: { 0: { cellWidth: 42, halign: "center" }, 2: { cellWidth: 78, halign: "right" }, 3: { cellWidth: 52, halign: "right" }, 4: { cellWidth: 92, halign: "right" } },
    });
    after();

    // Tables 2…5 — the items inside each category
    detail.forEach((d, i) => {
      caption(`Table ${i + 2} – ${d.c.title} (${d.c.code}) – ${fmt(d.total)} items`);
      doc.autoTable({
        ...base,
        startY: y,
        head: [["S. No.", "Sub-category", "Items collected", "Schools reporting", "Share"]],
        body: d.rows.map((r, n) => [n + 1, r.label, fmt(r.qty), fmt(r.entries), share(r.qty, d.total)]),
        foot: [["", "Total", R(fmt(d.total)), R(fmt(d.rows.reduce((t, r) => t + r.entries, 0))), R("100.0%")]],
        columnStyles: { 0: { cellWidth: 42, halign: "center" }, 2: { cellWidth: 78, halign: "right" }, 3: { cellWidth: 84, halign: "right" }, 4: { cellWidth: 52, halign: "right" } },
      });
      after();
    });

    // Table 6 — what schools typed in for themselves
    if (othRows.length) {
      caption(`Table ${detail.length + 2} – Others – items named by schools (OTH) – ${fmt(othTotal)} items`);
      doc.autoTable({
        ...base,
        startY: y,
        head: [["S. No.", "Item name reported by schools", "Items collected", "School entries"]],
        body: othRows.map((r, n) => [n + 1, r.label, fmt(r.qty), r.entries === null ? "–" : fmt(r.entries)]),
        foot: [["", "Total", R(fmt(othTotal)), R(fmt(othRows.reduce((t, r) => t + (r.entries || 0), 0)))]],
        columnStyles: { 0: { cellWidth: 42, halign: "center" }, 2: { cellWidth: 88, halign: "right" }, 3: { cellWidth: 88, halign: "right" } },
      });
      after();
    }

    const pages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(120, 135, 126);
      doc.text("Eco Clubs for Mission LiFE · Special Campaign 6 · E-Waste Collection Drive", M, H - 20);
      doc.text(`Page ${p} of ${pages}`, W - M, H - 20, { align: "right" });
    }
    doc.save(`Special_Campaign_6_EWaste_Category_Wise_${scopeFile()}_${stamp()}.pdf`);
  };
  const exportRows = () => post("swachhata/ewaste/admin/schools", listQuery(applied, rq, { export: true, page: 1 })).then((d) => d.rows || []);
  const exportSchools = async () => {
    setExporting("xlsx");
    try {
      const rows = await exportRows();
      const ws = XLSX.utils.json_to_sheet(rows.map((r, i) => {
        const cell = {};
        (r.items || []).forEach((it) => {
          if (it.category === "OTH") return;
          const col = `${it.category} – ${EWASTE_ITEM_LABEL[`${it.category}:${it.item}`] || it.item}`;
          cell[col] = it.item === "other" ? `${it.qty} (${it.name})` : it.qty;
        });
        const othersList = (r.items || []).filter((it) => it.category === "OTH").map((it) => `${it.name}: ${it.qty}`).join("; ");
        return {
          "S.No": i + 1, "UDISE Code": r.udise_id, "School Name": r.school_name, State: r.state, District: r.district, Block: r.block,
          "Activity Date": r.activityDate, Workshops: r.workshops, "Co-curricular Activity": ccLabel(r.coCurricular), Description: r.description,
          ...Object.fromEntries(AGE_GROUPS.map((g) => [`Participants ${g.label}`, r.participants?.[g.key] ?? 0])),
          "Total Participants": r.participants?.total ?? 0,
          "Collection Corner / Bin": cornerLabel(r.collectionCorner), "E-Waste Collected": r.collected === "yes" ? "Yes" : "No",
          "Total Items": r.totals?.items || 0,
          ...Object.fromEntries(EWASTE_CATEGORIES.map((c) => [`${c.code} total`, r.totals?.[c.code] || 0])),
          ...Object.fromEntries(EWASTE_CATEGORIES.filter((c) => !c.freeform).flatMap((c) => c.items.map((it) => {
            const col = `${c.code} – ${it.label}`;
            return [col, cell[col] ?? ""];
          }))),
          "OTH – Items (name: qty)": othersList,
          "Photos": (r.photos || []).join("\n"), "Receipt / Certificate": r.document || "",
          "First Submitted At": fmtDate(r.firstSubmittedAt), "Last Updated At": fmtDate(r.submittedAt), "Times Updated": r.updates,
        };
      }));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "E-Waste reports");
      XLSX.writeFile(wb, `Special_Campaign_6_EWaste_SchoolReports_${scopeFile()}_${stamp()}.xlsx`);
    } catch (e) { setError("Export failed — please try again."); }
    finally { setExporting(""); }
  };

  // The school register as a printable document: one row per school with its
  // category totals and the items it reported (same filters as the table).
  // Photos and receipts stay in the Excel export — links are no use on paper.
  const exportSchoolsPdf = async () => {
    setExporting("pdf");
    try {
      const rows = await exportRows();
      await new Promise((r) => setTimeout(r, 30)); // let "Preparing PDF…" paint before the long synchronous layout
      // the built-in PDF font only has Latin glyphs — other scripts would print as garbage
      const safe = (s) => String(s ?? "").replace(/[^\t\n\r\x20-\x7E\xA0-\xFF–—‘’“”•…]/g, "").replace(/\s+/g, " ").trim();
      const itemsText = (r) => (r.items || []).filter((it) => it.qty).map((it) => {
        const label = it.category === "OTH" || it.item === "other" ? safe(it.name) || "Other (regional script)" : EWASTE_ITEM_LABEL[`${it.category}:${it.item}`] || it.item;
        return `${label} ${fmt(it.qty)}`;
      }).join("; ");
      const sum = (f) => rows.reduce((a, r) => a + (Number(f(r)) || 0), 0);

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4", compress: true });
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();
      const M = 28;
      const INK = [15, 36, 24];
      const GREEN = [19, 138, 71];
      const filters = [
        rq.collected === "yes" ? "schools that collected E-Waste" : rq.collected === "no" ? "schools that did not collect E-Waste" : "all reporting schools",
        rq.search ? `search “${safe(rq.search)}”` : "",
        `sorted by ${{ submittedAt: "latest report", items: "most items", participants: "most participants" }[rq.sortBy] || "latest report"}`,
        applied.from || applied.to ? `reports ${applied.from || "…"} to ${applied.to || "…"}` : "",
      ].filter(Boolean).join(" · ");
      doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
      doc.text("Special Campaign 6 — E-Waste Collection Drive · School reports", M, 36);
      doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(95, 115, 104);
      doc.text(`${safe(scopeName)}${applied.district ? ` · ${titleCase(applied.district)}` : ""} · ${fmt(rows.length)} school${rows.length === 1 ? "" : "s"} · ${filters}`, M, 52);
      doc.text(`Categories (Nos.): ${EWASTE_CATEGORIES.map((c) => `${c.code} ${c.short}`).join(" · ")} · Generated ${new Date().toLocaleString("en-IN")}`, M, 65);
      doc.autoTable({
        startY: 76,
        head: [["#", "School", "District / State/UT/AB", "Activity date", "Collected", "Total items", ...CODES, "Partici-\npants", "Corner", "Items collected (Nos.)"]],
        body: rows.map((r, i) => [
          i + 1,
          `${safe(r.school_name) || "—"}\nUDISE ${r.udise_id}`,
          `${titleCase(r.district) || "—"}\n${titleCase(r.state)}`,
          r.activityDate ? new Date(r.activityDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—",
          r.collected === "yes" ? "Yes" : "No",
          fmt(r.totals?.items || 0),
          ...CODES.map((c) => (r.totals?.[c] ? fmt(r.totals[c]) : "—")),
          fmt(r.participants?.total || 0),
          cornerLabel(r.collectionCorner),
          itemsText(r) || "—",
        ]),
        foot: [["", `Total · ${fmt(rows.length)} schools`, "", "", fmt(rows.filter((r) => r.collected === "yes").length), fmt(sum((r) => r.totals?.items)), ...CODES.map((c) => fmt(sum((r) => r.totals?.[c]))), fmt(sum((r) => r.participants?.total)), "", ""]],
        styles: { fontSize: 6.8, cellPadding: 3, lineColor: [225, 232, 228], lineWidth: 0.5, textColor: INK, valign: "top", overflow: "linebreak" },
        headStyles: { fillColor: GREEN, textColor: 255, fontSize: 7, valign: "middle" },
        footStyles: { fillColor: [238, 245, 240], textColor: INK, fontStyle: "bold" },
        columnStyles: {
          0: { cellWidth: 24 }, 1: { cellWidth: 130 }, 2: { cellWidth: 82 }, 3: { cellWidth: 50 }, 4: { cellWidth: 40 },
          5: { cellWidth: 38, halign: "right" },
          ...Object.fromEntries(CODES.map((c, k) => [6 + k, { cellWidth: 33, halign: "right" }])),
          [6 + CODES.length]: { cellWidth: 40, halign: "right" }, [7 + CODES.length]: { cellWidth: 44 },
        },
        alternateRowStyles: { fillColor: [250, 252, 250] },
        showFoot: "lastPage",
        margin: { left: M, right: M, top: 32, bottom: 32 },
      });
      const pages = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pages; p++) {
        doc.setPage(p);
        doc.setFontSize(8); doc.setTextColor(120, 135, 126);
        doc.text("Eco Clubs for Mission LiFE · Special Campaign 6 · E-Waste Collection Drive · School reports", M, H - 14);
        doc.text(`Page ${p} of ${pages}`, W - M, H - 14, { align: "right" });
      }
      doc.save(`Special_Campaign_6_EWaste_SchoolReports_${scopeFile()}_${stamp()}.pdf`);
    } catch (e) { setError("PDF export failed — please try again."); }
    finally { setExporting(""); }
  };
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="swx ewd">
      {/* ═══ HERO ═══ */}
      <section className="swx-hero">
        <div className="swx-wrap">
          <div className="swx-hero__top">
            <div className="swx-hero__intro">
              <div className="swx-live"><i />Special Campaign 6 · E-Waste (Management) Rules, 2022</div>
              <div className="swx-hero__title">E-Waste Collection Drive</div>
              <div className="swx-hero__sub">{`School reports · item-wise E-Waste collected · ${scopeName}`}</div>
              <div className="swx-crumbs">
                {crumbs.map((c, i) => (
                  <React.Fragment key={c.label}>
                    {i > 0 && <Icon name="chevron" size={14} />}
                    {c.onClick ? <button type="button" onClick={c.onClick}>{c.label}</button> : <span>{c.label}</span>}
                  </React.Fragment>
                ))}
                {isStateAdmin && <span className="swx-crumbs__lock"><Icon name="lock" size={13} />State admin view</span>}
              </div>
            </div>
            <div className="swx-hero__actions">
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => navigate("/admin/environment-day-dashboard")}><Icon name="back" size={16} />Back to Reports</button>
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => navigate("/admin/special-campaign-6-report")} title="National Event on 28 October 2026 under Special Campaign 6 — school uploads, States/UTs/ABs-wise"><Icon name="report" size={16} />National Event 28 Oct report</button>
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => fetchAll(applied)} disabled={loading}><Icon name="refresh" size={16} />{loading ? "Refreshing…" : "Refresh"}</button>
            </div>
          </div>

          <div className="swx-filters">
            {!isStateAdmin && (
              <label className="swx-field">
                <span>States/UTs/ABs</span>
                <select value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value, district: "" })}>
                  <option value="">All States/UTs/ABs</option>
                  {stateOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  {draft.state && !stateOptions.some((r) => r.key === draft.state) && <option value={draft.state}>{titleCase(draft.state)}</option>}
                </select>
              </label>
            )}
            {(applied.state || isStateAdmin) && level === "district" && (
              <label className="swx-field">
                <span>District</span>
                <select value={draft.district} onChange={(e) => setDraft({ ...draft, district: e.target.value })}>
                  <option value="">All districts</option>
                  {(overview?.rows || []).filter((r) => r.key).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  {draft.district && !(overview?.rows || []).some((r) => r.key === draft.district) && <option value={draft.district}>{titleCase(draft.district)}</option>}
                </select>
              </label>
            )}
            <label className="swx-field"><span>From</span><input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
            <label className="swx-field"><span>To</span><input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
            <div className="swx-filters__btns">
              <button type="button" className="swx-btn swx-btn--orange" onClick={() => apply(draft)}>Apply filters</button>
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => apply(empty)}>Reset</button>
            </div>
          </div>

          <nav className="swx-tabs" aria-label="Sections">
            <button type="button" onClick={() => jump("ewd-overview")}><Icon name="map" size={16} />{`${areaWord}-wise overview`}</button>
            <button type="button" onClick={() => jump("ewd-items")}><Icon name="chip" size={16} />Items collected</button>
            <button type="button" onClick={() => jump("ewd-schools")}><Icon name="school" size={16} />School reports</button>
          </nav>
        </div>
      </section>

      <div className="swx-wrap">
        {/* ═══ HEADLINE ═══ */}
        <section className="swx-stats">
          <StatTile i={0} icon="map" tone={T.violet} loading={!overview} label={`${areaWordPlural} participating`} value={fmt(ot.areas)} sub={`${fmt(ot.schools)} schools reported`} />
          <StatTile i={1} icon="report" tone={T.blue} loading={!overview} label="Schools reported" value={compact(ot.schools)} sub={`${fmt(ot.workshops)} workshops held`} />
          <StatTile i={2} icon="chip" tone="#0e7490" loading={!overview} label="E-Waste items collected" value={compact(ot.items)} sub={`from ${fmt(ot.collectedSchools)} schools`} />
          <StatTile i={3} icon="school" tone={T.green} loading={!overview} label="Schools that collected E-Waste" value={compact(ot.collectedSchools)} sub={`${pct(ot.collectedSchools, ot.schools)}% of reporting schools`} foot={pct(ot.collectedSchools, ot.schools)} />
          <StatTile i={4} icon="bin" tone={T.orange} loading={!overview} label="Collection corners set up" value={compact(ot.corners)} sub={`${pct(ot.corners, ot.schools)}% of reporting schools`} foot={pct(ot.corners, ot.schools)} />
          <StatTile i={5} icon="users" tone="#c2410c" loading={!overview} label="People participated" value={compact(ot.participants)} sub={`${compact(ot.students)} students · ${compact(ot.teachers)} teachers`} />
        </section>

        {error && <div className="swx-alert">{error}</div>}

        {/* ═══ 1. OVERVIEW ═══ */}
        <SectionHead
          id="ewd-overview"
          tone={T.violet}
          icon="map"
          eyebrow={`Step 1 · ${areaWord}-wise`}
          title={level === "district" ? `District-wise E-Waste collection · ${scopeName}` : "States/UTs/ABs-wise E-Waste collection"}
          desc={level === "district" ? "Every district with a school report. Click a district to focus on it." : "Every State/UT/AB with a school report — click a row to open its districts."}
          actions={(
            <>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportAreas} disabled={!areaRows.length}><Icon name="download" size={16} />Excel</button>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportAreasPdf} disabled={!itemCats.length} title="Category-wise report — the five categories of the E-Waste (Management) Rules, 2022"><Icon name="download" size={16} />Category-wise PDF</button>
            </>
          )}
        />
        <div className="swx-panel swx-panel--flush">
          <div className="swx-toolbar">
            <div className="swx-search"><Icon name="search" size={16} /><input type="search" placeholder={`Search ${areaWordPlural.toLowerCase()}…`} value={areaQuery} onChange={(e) => setAreaQuery(e.target.value)} /></div>
            <div className="swx-seg">
              {[["items", "Items"], ["schools", "Schools"], ["participants", "Participants"], ["name", "A–Z"]].map(([k, l]) => (
                <button key={k} type="button" className={areaSort === k ? "is-on" : ""} onClick={() => setAreaSort(k)}>{l}</button>
              ))}
            </div>
          </div>
          {!overview ? (
            <div className="swx-skel-rows">{Array.from({ length: 6 }, (_, i) => <div key={i} className="swx-skel swx-skel--row" />)}</div>
          ) : areaRows.length === 0 ? (
            <Empty text="No E-Waste reports yet for this scope." icon="map" />
          ) : (
            <div className="swx-table-wrap swx-table-wrap--tall">
              <table className="swx-table swx-table--areas">
                <thead>
                  <tr>
                    <th className="c-rank">#</th>
                    <th>{areaWord}</th>
                    <th className="c-bar">Schools reported</th>
                    <th className="c-bar">Items collected</th>
                    {EWASTE_CATEGORIES.map((c) => <th key={c.code} className="num"><span className="ewd-th" style={{ "--tone": c.tone }}>{c.code}</span></th>)}
                    <th className="num">Corners</th>
                    <th className="num">Participants</th>
                    <th className="c-go" aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {areaRows.map((r, i) => {
                    const clickable = !!r.key && ((level === "state" && !isStateAdmin) || (level === "district" && !applied.district));
                    return (
                      <tr key={r.key || `none-${i}`} className={clickable ? "is-link" : ""} onClick={() => clickable && drill(r)} style={{ "--i": Math.min(i, 20) }}>
                        <td className="c-rank"><span className={`swx-rank ${i < 3 && areaSort !== "name" ? `swx-rank--${i + 1}` : ""}`}>{i + 1}</span></td>
                        <td><div className="swx-area">{r.name}</div></td>
                        <td className="c-bar"><div className="swx-cellbar swx-cellbar--blue"><span className="swx-cellbar__track"><i style={{ width: `${(r.schools / maxSchools) * 100}%` }} /></span><b>{fmt(r.schools)}</b></div></td>
                        <td className="c-bar"><div className="swx-cellbar ewd-cellbar"><span className="swx-cellbar__track"><i style={{ width: `${(r.items / maxItems) * 100}%` }} /></span><b>{fmt(r.items)}</b></div></td>
                        {CODES.map((c) => <td key={c} className="num">{r[c] ? fmt(r[c]) : <span className="swx-muted">0</span>}</td>)}
                        <td className="num">{fmt(r.corners)}<small>{`${pct(r.corners, r.schools)}%`}</small></td>
                        <td className="num">{fmt(r.participants)}</td>
                        <td className="c-go">{clickable && <Icon name="chevron" size={16} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>{`Total · ${fmt(ot.areas)} ${areaWordPlural.toLowerCase()}`}</td>
                    <td className="c-bar"><b>{fmt(ot.schools)}</b></td>
                    <td className="c-bar"><b>{fmt(ot.items)}</b></td>
                    {CODES.map((c) => <td key={c} className="num">{fmt(ot[c])}</td>)}
                    <td className="num">{fmt(ot.corners)}</td>
                    <td className="num">{fmt(ot.participants)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ═══ 2. ITEMS ═══ */}
        <SectionHead
          id="ewd-items"
          tone="#0e7490"
          icon="chip"
          eyebrow="Step 2 · Item-wise"
          title="What schools collected"
          desc="Quantities in numbers (Nos.) by DARPG category — Schedule I, E-Waste (Management) Rules, 2022."
        />
        <div className="swx-minis">
          {EWASTE_CATEGORIES.map((c, i) => (
            <MiniKpi key={c.code} tone={c.tone} label={`${c.code} · ${c.short}`} value={compact(catTotals[i])} hint={`${pct(catTotals[i], ot.items)}% of items`} />
          ))}
          <MiniKpi tone="#0f2418" label="All categories" value={compact(ot.items)} hint={`${fmt(ot.photos)} photos uploaded`} />
        </div>

        <div className="swx-grid">
          <Panel span={5} title="Share by category" desc="Items collected in each category">
            {!overview ? <div className="swx-skel swx-skel--chart" /> : !ot.items ? <Empty text="No items reported yet." icon="chip" /> : (
              <ReactApexChart options={donutOptions} series={catTotals} type="donut" height={310} />
            )}
          </Panel>
          <Panel span={7} title="Reports per day" desc="Schools reporting for the first time and the items they reported (IST)">
            {!overview ? <div className="swx-skel swx-skel--chart" /> : trend.length === 0 ? <Empty text="No reports yet." icon="report" /> : (
              <ReactApexChart options={trendOptions} series={trendSeries} type="area" height={310} />
            )}
          </Panel>

          {EWASTE_CATEGORIES.filter((c) => !c.freeform).map((c) => {
            const data = itemCats.find((x) => x.code === c.code);
            const rows = (data?.items || [])
              .map((it) => ({ key: it.key, label: EWASTE_ITEM_LABEL[`${c.code}:${it.key}`] || it.key, count: it.qty, entries: it.entries }))
              .sort((a, b) => b.count - a.count);
            return (
              <Panel key={c.code} span={6} title={<span className="ewd-panel-title"><span className="ewd-code" style={{ "--tone": c.tone }}>{c.code}</span>{c.title}</span>} desc={`${fmt(data?.total || 0)} items · sorted by quantity`}>
                {!items ? <div className="swx-skel swx-skel--chart" /> : !data?.total ? <Empty text="Nothing reported in this category yet." icon="chip" /> : (
                  <Bars rows={rows} total={0} tone={c.tone} valueLabel={(r) => fmt(r.count)} sub={(r) => `${fmt(r.entries)} school${r.entries === 1 ? "" : "s"}`} />
                )}
              </Panel>
            );
          })}

          <Panel span={6} title={<span className="ewd-panel-title"><span className="ewd-code" style={{ "--tone": CAT.OTH.tone }}>OTH</span>Others — items named by schools</span>} desc="Most reported names under Others and each category's “Other” row">
            {!items ? <div className="swx-skel swx-skel--chart" /> : !(items.otherNames || []).length ? <Empty text="No named items yet." icon="chip" /> : (
              <ol className="swx-leader ewd-names">
                {items.otherNames.slice(0, 12).map((n, i) => (
                  <li key={`${n.category}-${n.name}-${i}`} style={{ "--i": i }}>
                    <span className="ewd-code" style={{ "--tone": CAT[n.category]?.tone }}>{n.category}</span>
                    <div className="swx-leader__main"><b>{n.name}</b><small>{`${fmt(n.entries)} school entr${n.entries === 1 ? "y" : "ies"}`}</small></div>
                    <div className="swx-leader__num"><b>{fmt(n.qty)}</b><small>items</small></div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
          <Panel span={6} title="Collection corner / bin" desc="Did the school set up an E-Waste collection corner?">
            {!overview ? <div className="swx-skel swx-skel--row" /> : !ot.schools ? <Empty text="No reports yet." icon="bin" /> : (
              <>
                <div className="swx-stack">
                  {cornerRows.map(([k, l, c]) => {
                    const w = pct(overview.corner?.[k], ot.schools);
                    return w > 0 ? <span key={k} style={{ width: `${w}%`, background: c }}>{w >= 10 ? `${l} · ${w}%` : ""}</span> : null;
                  })}
                </div>
                <div className="swx-legend">
                  {cornerRows.map(([k, l, c]) => <span key={k}><i style={{ background: c }} />{`${l} · ${fmt(overview.corner?.[k] || 0)} schools`}</span>)}
                </div>
                <div className="ewd-cc">
                  <div className="swx-panel__title" style={{ fontSize: 14, marginTop: 18 }}>Co-curricular activities</div>
                  <Bars rows={CO_CURRICULAR.map((c) => ({ key: c.key, label: c.label, count: overview.coCurricular?.[c.key] || 0 })).sort((a, b) => b.count - a.count)} total={ot.schools} tone={T.teal} />
                </div>
              </>
            )}
          </Panel>
        </div>

        {/* ═══ 3. SCHOOLS ═══ */}
        <SectionHead
          id="ewd-schools"
          tone={T.blue}
          icon="school"
          eyebrow="Step 3 · School-wise"
          title="School reports"
          desc="Every school's E-Waste Collection Drive report — Excel carries every item column, participants, photos and receipts; PDF is a printable school-wise summary. Both follow the filters below."
          actions={(
            <>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportSchools} disabled={!!exporting || !list.total}><Icon name="download" size={16} />{exporting === "xlsx" ? "Exporting…" : "Excel"}</button>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportSchoolsPdf} disabled={!!exporting || !list.total}><Icon name="download" size={16} />{exporting === "pdf" ? "Preparing PDF…" : "PDF"}</button>
            </>
          )}
        />
        <div className="swx-panel swx-panel--flush">
          <div className="swx-toolbar">
            <div className="swx-search"><Icon name="search" size={16} /><input type="search" placeholder="School name, district or 11-digit UDISE" value={rq.search} onChange={(e) => setRq({ ...rq, search: e.target.value, page: 1 })} /></div>
            <div className="swx-seg">
              {[["", "All"], ["yes", "Collected"], ["no", "Not collected"]].map(([k, l]) => (
                <button key={k || "all"} type="button" className={rq.collected === k ? "is-on" : ""} onClick={() => setRq({ ...rq, collected: k, page: 1 })}>{l}</button>
              ))}
            </div>
            <div className="swx-seg">
              {[["submittedAt", "Latest"], ["items", "Most items"], ["participants", "Most participants"]].map(([k, l]) => (
                <button key={k} type="button" className={rq.sortBy === k ? "is-on" : ""} onClick={() => setRq({ ...rq, sortBy: k, page: 1 })}>{l}</button>
              ))}
            </div>
          </div>
          {list.rows.length === 0 ? <Empty text={listLoading ? "Loading reports…" : "No reports match these filters."} icon="school" /> : (
            <div className={`swx-table-wrap ${listLoading ? "is-busy" : ""}`}>
              <table className="swx-table">
                <thead>
                  <tr>
                    <th>School</th><th>District / State/UT/AB</th><th>Activity</th><th>Collected</th>
                    <th className="num">Items</th><th>By category</th><th className="num">Participants</th><th>Corner</th><th>Photos</th><th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {list.rows.map((r) => (
                    <tr key={r.udise_id}>
                      <td className="c-wide"><b className="swx-strong">{r.school_name}</b><small>{`UDISE ${r.udise_id}`}</small></td>
                      <td>{titleCase(r.district) || "—"}<small>{titleCase(r.state)}</small></td>
                      <td className="c-nowrap">{r.activityDate ? new Date(r.activityDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}<small>{`${fmt(r.workshops)} workshop${r.workshops === 1 ? "" : "s"}${r.coCurricular ? ` · ${ccLabel(r.coCurricular)}` : ""}`}</small></td>
                      <td><span className={`swx-yn ${r.collected === "yes" ? "is-yes" : "is-no"}`}>{r.collected === "yes" ? "Yes" : "No"}</span></td>
                      <td className="num"><b>{fmt(r.totals?.items || 0)}</b></td>
                      <td className="ewd-chips-cell">
                        {CODES.filter((c) => r.totals?.[c]).map((c) => (
                          <span key={c} className="ewd-chip" style={{ "--tone": CAT[c].tone }} title={CAT[c].title}>{`${c} ${fmt(r.totals[c])}`}</span>
                        ))}
                        {!r.totals?.items && <span className="swx-muted">—</span>}
                      </td>
                      <td className="num">{fmt(r.participants?.total || 0)}</td>
                      <td>{cornerLabel(r.collectionCorner)}</td>
                      <td className="c-nowrap">
                        {(r.photos || []).map((u, i) => <a key={u} className="swx-file" href={u} target="_blank" rel="noopener noreferrer"><Icon name="camera" size={13} />{i + 1}</a>)}
                        {r.document && <a className="swx-file ewd-doc" href={r.document} target="_blank" rel="noopener noreferrer">Receipt</a>}
                      </td>
                      <td className="c-nowrap">{fmtDate(r.submittedAt)}{r.updates ? <small>{`updated ${r.updates}×`}</small> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={list.page} totalPages={list.totalPages} total={list.total} noun="school" loading={listLoading} onPage={(p) => setRq({ ...rq, page: p })} />
        </div>

        <div className="swx-foot">
          {`Figures refresh every ~2 minutes (item-wise every ~10)${overview?.generatedAt ? ` · last built ${fmtDate(overview.generatedAt)}` : ""} · Source: E-Waste Collection Drive school reports`}
        </div>
      </div>
    </div>
  );
}
